import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  Download as DownloadIcon
} from '@mui/icons-material';

const PayslipDialog = ({ open, onClose, payrollId, employeeId, employeeName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [iframeReload, setIframeReload] = useState(0);

  useEffect(() => {
    const onPayslipSettingsUpdated = (e) => {
      const id = e.detail?.payrollId;
      if (id != null && String(id) === String(payrollId)) {
        setIframeReload((n) => n + 1);
      }
    };
    window.addEventListener('payroll-payslip-settings-updated', onPayslipSettingsUpdated);
    return () => window.removeEventListener('payroll-payslip-settings-updated', onPayslipSettingsUpdated);
  }, [payrollId]);

  // Don't render if we don't have the required IDs
  if (!payrollId || !employeeId || payrollId === 'undefined' || employeeId === 'undefined') {
    console.error('PayslipDialog: Missing payrollId or employeeId', { payrollId, employeeId });
    return null;
  }

  const handlePrint = () => {
    const iframe = document.getElementById('payslip-iframe');
    if (iframe && iframe.contentWindow) {
      // Send print message to iframe
      iframe.contentWindow.postMessage({ action: 'print' }, '*');
    }
  };

  const iframeUrl = `/payslip/${payrollId}/${employeeId}${iframeReload ? `?_cb=${iframeReload}` : ''}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          height: isMobile ? '100vh' : '90vh',
          maxHeight: '90vh',
          borderRadius: isMobile ? 0 : 2
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: '#f8f9fa',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PrintIcon sx={{ color: '#12C1DC' }} />
              <Box>
                <Box component="span" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                  Payslip
                </Box>
                {employeeName && (
                  <Box component="span" sx={{ ml: 1, color: '#6b7280', fontSize: '0.9rem' }}>
                    - {employeeName}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={handlePrint}
            size="small"
            sx={{ 
              color: '#12C1DC',
              '&:hover': { bgcolor: 'rgba(18, 193, 220, 0.1)' },
              border: '1px solid #e5e7eb',
              borderRadius: 1
            }}
            title="Print Payslip"
          >
            <PrintIcon />
          </IconButton>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ 
              color: '#6b7280',
              '&:hover': { bgcolor: 'rgba(107, 114, 128, 0.1)' }
            }}
            title="Close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ 
          width: '100%', 
          height: '100%',
          position: 'relative'
        }}>
          <iframe
            id="payslip-iframe"
            key={iframeReload}
            src={iframeUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              display: 'block'
            }}
            title={`Payslip for ${employeeName || 'Employee'}`}
            onLoad={() => {
              // Hide the toolbar in the iframe for cleaner display
              const iframe = document.getElementById('payslip-iframe');
              if (iframe && iframe.contentWindow) {
                try {
                  const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                  const toolbar = iframeDoc.querySelector('.no-print');
                  if (toolbar) {
                    toolbar.style.display = 'none';
                  }
                } catch (e) {
                  // Cross-origin restrictions might prevent this
                  console.log('Cannot access iframe content due to cross-origin restrictions');
                }
              }
            }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PayslipDialog;
